/** @jsxImportSource @emotion/react */
import { useDispatch, useSelector } from 'react-redux';
import { ACTION_TYPE } from '../../core/redux/actionType';
import ModalView, { ModalContent } from '../../layout/modal/ModalView';
import { COLUMN_SPACING } from '../../forms/styles';
import { getResource } from '../../language/resources/getResource';
import { IAppState } from '../../core/redux/IAppState';
import { MODAL_SIZE } from '../../layout/modal/modalSize';
import { GuestPaymentRegistration, PaymentMethodRegistration } from './paymentRegistration';
import { StyledBoxedMessage } from '../../forms/elements/boxedMessage/StyledBoxedMessage';
import { BoxedErrorInverse, BoxedErrorMessageDefault } from '../../forms/elements/boxedMessage/boxedMessageStyles';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import xCircleImg from '../../assets/icons/xCircleRed.svg';
import { BLACK, RED_CREEK } from '../../styles/colors';
import { FADE_IN_KEYFRAMES } from '../../styles/animations';
import { DISABLED, LargeText, XXLargeText } from '../../styles/styles';
import { getError, getLoading } from '../../core/redux/api/module';
import { deactivateGuestPaymentRegistrationAction } from './deactivateGuestPaymentRegistrationAction';
import { getPaymentGuestAction } from './getPaymentGuestAction';
import PaymentMethodView from './PaymentMethodView';
import closeBtn from '../../assets/icons/modalCloseBtn.svg';
import { Column, Row } from '../../layout/flexbox';
import { PaymentGuest } from './paymentGuest';
import { PaymentRegistrationView } from './PaymentRegistrationView';
import { useCallback, useEffect } from 'react';
import { clearApiErrorAction } from '../../core/redux/api/clearApiErrorAction';
import { ApiError } from '../../core/redux/api/apiError';
import ConfirmationModal from '../../forms/elements/ConfirmationModal';
import React from 'react';


const deactivateIcon = css`
  width: 38px; 
  height: 38px; 
  pointer-events: initial;
  margin: auto;
  margin-bottom: -10px; /* align image with text */
`;

const deactivateButton = css`
  width: 250px;
  color: ${RED_CREEK};
  background-color: transparent;;
  border: none;
  text-align: center;
  text-decoration: none;
  ${LargeText}
  animation: 1s ${FADE_IN_KEYFRAMES};
  cursor: pointer;
  
  &:hover {
    opacity: 0.75;
  }
  
  &:disabled {
    ${DISABLED}
  }
`;

const deactivateButtonTextPosition = css`
  height: 50%;
  margin-left: 10px;
  margin-bottom: 10px;
  vertical-align: middle;
  display: inline-block;
`;

const registrationsRow = css`
  margin-top: 10px;
  margin-bottom: 30px;
  overflow: auto;
`;

const registeredGuestsText = css`
  ${XXLargeText}; 
  color: ${BLACK};
`;

const CloseButtonSizing = css`
  height: 40px;
  width: 40px;
  padding: 8px;
  margin-top: -75px;
  margin-left: 20px;
  margin-right: 20px;
  border-radius: 50%;
  border-width: 0;
`;

const CloseButton = styled('button')`
  ${CloseButtonSizing};
  background-color: transparent;
  cursor: pointer;
  
  &:hover {
    background-color: rgba( 0, 0, 0, .06);
  }
`;


type Props = {
  guestPaymentRegistration: GuestPaymentRegistration;
  guestId: string;
  onDismiss: () => void;
}


/**
 * PaymentMethodModal: a component that displays the registrations for a payment method.
 */  
export const PaymentMethodModal = (props: Props) => {

  const {
    guest,
    guestDetails,
    paymentGuests,
    resources,
    isPaymentMethodLoading,
    paymentMethodError,
  } = useSelector((state: IAppState) => ({
      guest:                  state.guests.get(props.guestId),
      guestDetails:           state.guests,
      paymentGuests:          state.paymentGuests,
      resources:              state.locale,
      isPaymentMethodLoading: !!getLoading(state, props.guestPaymentRegistration.paymentMethodId.toString(), ACTION_TYPE.GET_PAYMENT_METHOD_REQUEST),
      paymentMethodError:     getError(state, props.guestPaymentRegistration.paymentMethodId.toString(), ACTION_TYPE.GET_PAYMENT_METHOD_REQUEST),
  }));

  const [showConfirmationModal, setShowConfirmationModal] = React.useState(false);

  const dispatch = useDispatch();

  const deactivateAllPaymentRegistrations = (registrations: PaymentMethodRegistration[]) => {
        registrations.forEach((registration: PaymentMethodRegistration) => {
          dispatch(deactivateGuestPaymentRegistrationAction(registration));
        });
        dismissConfirmationModal();
      };
  const getPaymentGuest = useCallback((masterGuestId: string) => dispatch(getPaymentGuestAction(masterGuestId)),[dispatch]);
  const clearError = (error: ApiError) => dispatch(clearApiErrorAction(error.requestId, error.actionType));
  
  useEffect(() => {
    if (guest.paymentMethod && guest.paymentMethod.registrations) {
      guest.paymentMethod.registrations.forEach((registration) => {
        if (!guestDetails.get(registration.masterGuestId) 
          && (!paymentGuests || (paymentGuests
            && paymentGuests?.findIndex((pg) => pg.masterGuestId === registration.masterGuestId) < 0))) {
          getPaymentGuest(registration.masterGuestId);
        }
      });
    }},[getPaymentGuest, guest.paymentMethod, guestDetails, paymentGuests]);


  const getGuestData = (masterGuestId: string): PaymentGuest => {
      const guestDetail = guestDetails.get(masterGuestId);
      if (guestDetail) {
        return {
          masterGuestId: masterGuestId, 
          firstName: guestDetail.firstName,
          lastName: guestDetail.lastName,
          photoData: guestDetail.photo?.imgSmall
          || guestDetail.photo?.imgLarge,
        } as PaymentGuest;
      }
  
      return {
        masterGuestId: masterGuestId
      } as PaymentGuest;
  };

  const closeModal = () => {
    if (paymentMethodError) {
      clearError(paymentMethodError);
    }
    props.onDismiss();
  }

  const displayConfirmationModal = () => setShowConfirmationModal(true);
  const dismissConfirmationModal = () => setShowConfirmationModal(false);

  return (
    <ModalView
        size={MODAL_SIZE.SMALL}
        contentCss={COLUMN_SPACING}
        onDismiss={closeModal}>
        <ModalContent>
        { 
            guest.paymentMethod ?  
            <Column>
              <Row alignItems='center' justifyContent='space-between'>
                {
                  <PaymentMethodView 
                    resources = {resources} 
                    paymentMethod={guest.paymentMethod}
                  />
                }  
                <CloseButton css={css`flex-basis: 40px;`}
                  title={getResource(resources, 'MODAL_TITLE_HEADER_CLOSE')}
                  onClick={closeModal}>
                    <img src={closeBtn} alt='Close'/>
                </CloseButton>
              </Row>

              <Row alignItems='center' justifyContent='space-between'>
                <span css={registeredGuestsText}>{getResource(resources, 'PAYMENT_REGISTRATION_GUESTS')}</span>
                <button
                    type="button"
                    disabled={(guest.paymentMethod.registrations || []).length === 0}
                    title={getResource(resources, 'PAYMENT_REGISTRATION_DEACTIVATE_ALL')}
                    onClick={displayConfirmationModal}
                    css={deactivateButton}>
                    <img css={deactivateIcon} src={xCircleImg} alt={'X'}
                        title={getResource(resources, 'PAYMENT_REGISTRATION_DEACTIVATE_ALL')}/>
                    <span css={deactivateButtonTextPosition}>{getResource(resources, 'PAYMENT_REGISTRATION_DEACTIVATE_ALL')}</span>
                </button>
              </Row>
              {
                (guest.paymentMethod.registrations || []).length === 0 
                    ? <StyledBoxedMessage extraCss={css`margin-top: 75px; margin-bottom: 75px;`} message={getResource(resources, 'PAYMENT_REGISTRATION_NO_REGISTRATIONS')} boxStyle={BoxedErrorInverse}/>
                    : <Row rootCss={registrationsRow}>
                        {
                          guest.paymentMethod.registrations.map((registration: PaymentMethodRegistration) => {
                            return <PaymentRegistrationView
                                key={registration.registrationId}
                                guest={getGuestData(registration.masterGuestId)}
                                registration={registration}
                            />;
                        })}
                      </Row>
              }
              <StyledBoxedMessage message={getResource(resources, 'PAYMENT_REGISTRATION_WARNING')} boxStyle={BoxedErrorInverse}/>
            </Column>
            :
            !isPaymentMethodLoading &&
            <Column>
              <div css={css`display: flex; justify-content: flex-end;`}>
                <CloseButton css={css`margin-top: 10px;flex-basis: 40px;`}
                  title={getResource(resources, 'MODAL_TITLE_HEADER_CLOSE')}
                  onClick={closeModal}>
                    <img src={closeBtn} alt='Close'/>
                </CloseButton>
              </div>
              <StyledBoxedMessage extraCss={css`margin-top: 15%;`} message={getResource(resources, 'PAYMENT_REGISTRATION_ERROR_PAYMENT_METHOD_NOT_FOUND')} boxStyle={BoxedErrorMessageDefault}/>
            </Column>
        }
        {
          showConfirmationModal
          && <ConfirmationModal
            rootCss={css`max-width: 700px;`}
            resources={resources}
            title={getResource(resources, 'PAYMENT_REGISTRATION_DEACTIVATE_ALL')}
            icon={xCircleImg}
            message={getResource(resources, 'PAYMENT_REGISTRATION_DEACTIVATE_ALL_CONFIRMATION')}
            styledMessage={getResource(resources, 'PAYMENT_REGISTRATION_WARNING')}
            style={BoxedErrorInverse}
            positiveButtonText={getResource(resources, 'PAYMENT_REGISTRATION_DEACTIVATE_ALL')}
            negativeButtonText={getResource(resources, 'PAYMENT_REGISTRATION_DEACTIVATE_CANCEL')}      
            onPositiveButtonClick={() => deactivateAllPaymentRegistrations(guest.paymentMethod?.registrations || [])}
            onNegativeButtonClick={dismissConfirmationModal}
            onDismiss={dismissConfirmationModal}/>
        }
        </ModalContent>
    </ModalView>
)};
